import api from "./api";

const appointmentService = {};
const endpoint = "/appointments";

appointmentService.createAppointment = function (appointment) {
  return api.post(endpoint, appointment).then(({ data }) => data);
};

appointmentService.getAppointmentById = function ({ id, params }) {
  return api.get(`${endpoint}/${id}`, { params }).then(({ data }) => data);
};

appointmentService.getAppointmentsByPatient = function ({
  patientId,
  params = { sort: { startsAt: -1 } },
  options = {},
}) {
  return api
    .get(`${endpoint}/patient/${patientId}`, { params, ...options })
    .then(({ data }) => data);
};

/**
 * @description Obtiene turnos del paciente logueado. Intenta /appointments/patient/:id,
 *              si falla intenta /appointments?patient=:id. Suprime toasts de error.
 * @param {string} userId - ID del usuario logueado
 * @returns {Promise<Array>} Lista de turnos
 */
appointmentService.getMyAppointments = function (userId) {
  const silentOptions = { getErrorMessage: () => null };
  return appointmentService
    .getAppointmentsByPatient({
      patientId: userId,
      options: silentOptions,
    })
    .catch(() => {
      return api
        .get(endpoint, {
          params: { patient: userId, sort: { startsAt: -1 } },
          ...silentOptions,
        })
        .then(({ data }) => data);
    });
};

appointmentService.getAppointments = function ({ params }) {
  return api.get(endpoint, { params }).then(({ data }) => data);
};

appointmentService.cancelAppointment = function (id) {
  return api.delete(`${endpoint}/${id}`).then(({ data }) => data);
};

const getErrorMessage = (err) => {
  const status = err.response?.status;
  const appointment = err.response?.data?.appointment;

  if (status === 409 && appointment) {
    if (appointment.status === "done") {
      return "Esta atención tiene un estado de finalizada.";
    }
    if (appointment.status === "cancelled") {
      return "Esta atención tiene un estado de cancelada.";
    }
  }

  return "No se puede iniciar la atención.";
};

appointmentService.startAppointment = function (id) {
  return api
    .put(`${endpoint}/${id}/start`, {}, { getErrorMessage })
    .then(({ data }) => data);
};

appointmentService.endAppointment = function (id) {
  return api.put(`${endpoint}/${id}/end`).then(({ data }) => data);
};

appointmentService.admitAppointment = function (id) {
  return api
    .put(`${endpoint}/${id}/admit`, {}, { getErrorMessage })
    .then(({ data }) => data);
};

const getCallErrorMessage = (err) => {
  const statusCode = err.response?.status;
  const errorCode = err.response?.data?.error;

  if (statusCode === 403) {
    return "La funcionalidad de llamadas no está habilitada para su organización.";
  }

  if (statusCode === 400) {
    if (
      errorCode === "CallProviderTypeMissing" ||
      errorCode === "CallProviderConfigMissing" ||
      errorCode === "CallProviderTypeEmpty" ||
      errorCode === "CallProviderApiKeyMissing" ||
      errorCode === "CallProviderUrlMissing"
    ) {
      return "La configuración del proveedor de llamadas no está completa. Contacte al administrador.";
    }

    if (errorCode === "DoctorBoxNotSelected") {
      return "No tiene ningún box seleccionado. Por favor seleccione uno e intente de nuevo.";
    }
  }

  if (statusCode === 404) {
    return "No se encontró la atención solicitada.";
  }

  return "Error al enviar la llamada. Por favor, intente nuevamente.";
};

appointmentService.callPatient = function (id) {
  return api
    .post(
      `${endpoint}/${id}/call`,
      {},
      { getErrorMessage: getCallErrorMessage }
    )
    .then(({ data }) => data);
};

/**
 * @description Datos públicos para la landing de videoconsulta del paciente:
 * médico, horario, adjuntos y sala activa. Acceso sin auth vía patientId.
 * @param {string} appointmentId - ID del turno
 * @param {string} patientId - ID del paciente (User._id)
 * @returns {Promise<Object>} { doctor, medicalSpecialty, startsAt, attentionType, attachments, room }
 */
appointmentService.getVideoConsultationLanding = function (
  appointmentId,
  patientId
) {
  return api
    .get(`${endpoint}/${appointmentId}/landing`, { params: { patientId } })
    .then(({ data }) => data);
};

/**
 * @description Sube un documento adjunto a la consulta (flujo paciente).
 * @param {string} appointmentId - ID del turno
 * @param {string} patientId - ID del paciente (User._id)
 * @param {File} file - Archivo a subir
 * @returns {Promise<Object>} { attachments } - lista actualizada
 */
appointmentService.uploadAppointmentAttachment = function (
  appointmentId,
  patientId,
  file
) {
  const form = new FormData();
  form.append("file", file);
  form.append("patientId", patientId);
  return api
    .post(`${endpoint}/${appointmentId}/attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then(({ data }) => data);
};

/**
 * @description Quita un documento adjunto de la consulta (flujo paciente).
 * @param {string} appointmentId - ID del turno
 * @param {string} attachmentId - ID del adjunto
 * @param {string} patientId - ID del paciente (User._id)
 * @returns {Promise<Object>} { attachments } - lista actualizada
 */
appointmentService.deleteAppointmentAttachment = function (
  appointmentId,
  attachmentId,
  patientId
) {
  return api
    .delete(`${endpoint}/${appointmentId}/attachments/${attachmentId}`, {
      params: { patientId },
    })
    .then(({ data }) => data);
};

/**
 * @description Lista los adjuntos de la consulta con URLs firmadas (flujo médico).
 * @param {string} appointmentId - ID del turno
 * @returns {Promise<Object>} { attachments } - cada uno con `url` firmada
 */
appointmentService.getAppointmentAttachments = function (appointmentId) {
  return api
    .get(`${endpoint}/${appointmentId}/attachments`)
    .then(({ data }) => data);
};

/**
 * @description Heartbeat de presencia del paciente en la videoconsulta. Le
 * avisa al backend que el paciente está conectado a la sala de espera, para
 * que el médico lo vea "En sala" en Atenciones.
 * @param {string} appointmentId - ID del turno
 * @param {string} patientId - ID del paciente (User._id)
 * @returns {Promise<Object>} { ok: true }
 */
appointmentService.markVideoPresence = function (appointmentId, patientId) {
  return api
    .post(`${endpoint}/${appointmentId}/presence`, { patientId })
    .then(({ data }) => data);
};

export default appointmentService;
